import { memo, useEffect } from 'react'
import type { ReactNode} from 'react';

import { useUserContext } from '@introducers-contexts'
import { getCurrentHub, setTags, setUser } from '@sentry/react'
import Cookies from 'js-cookie'
import { useLocation, useNavigate } from 'react-router-dom'

import { FullPageLoadingSpinner } from '@acre/design-system'

type Props = {
  children: ReactNode
}

const RequireAuth = ({ children }: Props) => {
  const user = useUserContext()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    if (user) {
      const userContext = {
        id: user.id,
        email: user.email_address!,
        organisation_id: user.organisation_id,
        organisation_name: user.organisation?.name,
        representative_of: user.organisation?.representative_of,
        representative_of_name: user.organisation?.representative_of_org?.name,
      }

      setUser(userContext)
      // custom context content is not indexed by sentry so we need to also set them as tags
      setTags({
        organisation_id: user.organisation_id,
        representative_of: user.organisation?.representative_of,
      })

      // Set user context on the current transaction to ensure it's captured
      // even if the transaction started before user data was available
      const transaction = getCurrentHub().getScope().getTransaction()
      if (transaction) {
        // setContext is deprecated but still works in v7 - it sets context data on the transaction
        // In v8, this will be handled differently via scope/attributes
        transaction.setContext('user', userContext)
      }
    }
  }, [location.pathname, user])

  useEffect(() => {
    if (!user) {
      // set a cookie with the current URL and redirect
      // to the login page
      Cookies.set('login_redirect', location.pathname)
      navigate('/login')
    }
  }, [location.pathname, navigate, user])

  if (!user) {
    return <FullPageLoadingSpinner />
  }

  return <>{children}</>
}

export default memo(RequireAuth)
